import { memo, useEffect, useRef, useState } from "react";
import { beginInteraction, endInteraction, setDiagModule } from "@/lib/diag";
import api, { apiError } from "@/lib/api";
import { money } from "@/lib/format";
import { toast } from "sonner";
import { Card } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import { useAuth } from "@/context/AuthContext";
import BulkTiersEditor from "@/components/BulkTiersEditor";
import { ReservedStockDialog } from "@/components/ReservedStockDialog";
import { Badge } from "@/components/ui/badge";
import { Label } from "@/components/ui/label";
import { Checkbox } from "@/components/ui/checkbox";
import { Tabs, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "@/components/ui/dialog";
import { Search, Plus, Package, AlertTriangle, PackageX, Download, RefreshCw, SlidersHorizontal, Trash2, Archive, ArchiveRestore, Layers } from "lucide-react";
import { useFashion } from "@/lib/fashion";
import { imgSrc } from "@/lib/img";
import DepartmentsPanel from "@/components/fashion/DepartmentsPanel";
import VariantManager from "@/components/fashion/VariantManager";
import ReturnHolds from "@/components/fashion/ReturnHolds";

const stockBadge = (s) =>
  s === "OUT OF STOCK" ? "bg-rose-500/15 text-rose-500 border-rose-500/30"
  : s === "LOW STOCK" ? "bg-amber-500/15 text-amber-600 dark:text-amber-400 border-amber-500/30"
  : "bg-emerald-500/15 text-emerald-600 dark:text-emerald-400 border-emerald-500/30";

const ADJUST_REASONS = ["Stock Count", "Damaged", "Received", "Correction", "Expired", "Lost / Theft", "Other"];

const blank = { bulk_pricing_enabled: false, bulk_tiers: [], name: "", category: "General", brand: "", supplier: "", cost: 0, price: 0, pricing_method: "price", markup_pct: "", product_type: "stock", barcode: "", sku: "", quantity: 0, reorder_level: 0, low_stock_level: 0, tax_status: "taxable", location: "", variant: "", model: "", unit: "each", department: null, is_parent: false, details: {} };

// Fashion & Beauty: authorized department correction (Owner/Manager) — recorded, history preserved, variants follow the parent.
function DepartmentCorrection({ form, fashion, onDone }) {
  const [open, setOpen] = useState(false);
  const [dept, setDept] = useState(form.department);
  const [reason, setReason] = useState("");
  const [busy, setBusy] = useState(false);
  const submit = async () => {
    setBusy(true);
    try { await api.put(`/products/${form.id}/department`, { department: dept, reason }); toast.success("Department corrected — change recorded"); onDone(); }
    catch (e) { toast.error(apiError(e.response?.data?.detail)); } finally { setBusy(false); }
  };
  return (
    <div className="rounded-xl border border-dashed border-border p-3 space-y-2" data-testid="pf-dept-correction">
      {!open ? <Button type="button" size="sm" variant="outline" onClick={() => setOpen(true)} data-testid="pf-dept-correct-btn">Correct department</Button> : (
        <>
          <p className="text-xs text-muted-foreground">Moves this product{form.is_parent ? " and its variants" : ""} to another department. Historical transactions are unchanged; the correction is recorded in the audit log.</p>
          <select className="w-full h-9 rounded-md border border-border bg-background px-2 text-sm" value={dept || ""} onChange={(e) => setDept(e.target.value)} data-testid="pf-dept-correct-select">
            {fashion.active.map((d) => <option key={d.key} value={d.key}>{d.name}</option>)}
          </select>
          <Input value={reason} onChange={(e) => setReason(e.target.value)} placeholder="Reason (required)" data-testid="pf-dept-correct-reason" />
          <div className="flex gap-2">
            <Button type="button" size="sm" disabled={busy || !dept || dept === form.department || reason.trim().length < 3} onClick={submit} data-testid="pf-dept-correct-save">Apply correction</Button>
            <Button type="button" size="sm" variant="ghost" onClick={() => setOpen(false)}>Cancel</Button>
          </div>
        </>
      )}
    </div>
  );
}

const round2 = (n) => Math.round((n + Number.EPSILON) * 100) / 100;
const grossMargin = (cost, price) => (price > 0 ? ((price - cost) / price) * 100 : 0);

// Debounced search input kept outside Inventory's state so each keystroke re-renders only this input, not the product table.
const SearchBox = memo(function SearchBox({ onChange, ...props }) {
  const [v, setV] = useState("");
  useEffect(() => { const t = setTimeout(() => onChange(v), 300); return () => clearTimeout(t); }, [v, onChange]);
  return <Input value={v} onChange={(e) => setV(e.target.value)} {...props} />;
});

export default function Inventory() {
  const [products, setProducts] = useState([]);
  const [tab, setTab] = useState("all");
  const [reorder, setReorder] = useState([]);
  const [q, setQ] = useState("");   // debounced search term actually sent to the API
  const PAGE_SIZE = 50;
  const [page, setPage] = useState(1);
  const loadSeq = useRef(0);
  const [meta, setMeta] = useState({ total: 0, pages: 1, counts: { all: 0, low: 0, out: 0, non_stock: 0 } });
  useEffect(() => { setDiagModule("inventory"); return () => setDiagModule(null); }, []);   // B2: API calls from this page belong to Inventory
  const [editOpen, setEditOpen] = useState(false);
  const [form, setForm] = useState(blank);
  const [adjustOpen, setAdjustOpen] = useState(false);
  const [adjTarget, setAdjTarget] = useState(null);
  const [reservedTarget, setReservedTarget] = useState(null);
  const [adj, setAdj] = useState({ dir: "add", amount: "", reason: "", note: "" });
  const { user } = useAuth();
  const fashion = useFashion();
  const [variantTarget, setVariantTarget] = useState(null);
  const [showArchivedDepts, setShowArchivedDepts] = useState(false);
  const deptOf = (p) => fashion.byKey(p?.department);
  const [impOpen, setImpOpen] = useState(false);
  const [impFile, setImpFile] = useState(null);
  const [impPrev, setImpPrev] = useState(null);
  const [impBusy, setImpBusy] = useState(false);
  const doPreview = async (f) => {
    setImpBusy(true); setImpPrev(null); setImpFile(f);
    try { const fd = new FormData(); fd.append("file", f);
      const { data } = await api.post("/products/import/preview", fd);
      setImpPrev(data);
    } catch (e) { toast.error(e.response?.data?.detail || "Preview failed"); }
    finally { setImpBusy(false); }
  };
  const doCommit = async () => {
    if (!impFile) return;
    setImpBusy(true);
    try { const fd = new FormData(); fd.append("file", impFile);
      const { data } = await api.post("/products/import/commit", fd);
      toast.success(`Imported ${data.inserted} products · ${data.skipped_existing} existing SKUs skipped`);
      setImpOpen(false); setImpPrev(null); setImpFile(null); window.location.reload();
    } catch (e) { toast.error(e.response?.data?.detail || "Import failed"); }
    finally { setImpBusy(false); }
  };
  const [adjHistory, setAdjHistory] = useState([]);
  const canManage = ["owner", "manager"].includes(user?.role);
  const [delOpen, setDelOpen] = useState(false);
  const [delTarget, setDelTarget] = useState(null);
  const [delReason, setDelReason] = useState("");
  const [delBusy, setDelBusy] = useState(false);
  const [delHasHistory, setDelHasHistory] = useState(false);

  const openDelete = (p) => { setDelTarget(p); setDelReason(""); setDelHasHistory(false); setDelOpen(true); };
  const doDelete = async () => {
    if (!delReason.trim()) { toast.error("A deletion reason is required"); return; }
    setDelBusy(true);
    try {
      await api.delete(`/products/${delTarget.id}`, { data: { reason: delReason.trim() } });
      toast.success(`Deleted ${delTarget.name}`); setDelOpen(false); load();
    } catch (e) {
      if (e.response?.status === 409 && e.response?.data?.detail?.has_history) {
        setDelHasHistory(true);
      } else { toast.error(apiError(e.response?.data?.detail) || "Delete failed"); }
    } finally { setDelBusy(false); }
  };
  const doArchive = async () => {
    setDelBusy(true);
    try {
      await api.post(`/products/${delTarget.id}/archive`, { reason: delReason.trim() });
      toast.success(`Archived ${delTarget.name}`); setDelOpen(false); load();
    } catch (e) { toast.error(apiError(e.response?.data?.detail) || "Archive failed"); }
    finally { setDelBusy(false); }
  };
  const doRestore = async (p) => {
    try {
      await api.post(`/products/${p.id}/restore`);
      toast.success(`Restored ${p.name}`); load();
    } catch (e) { toast.error(apiError(e.response?.data?.detail) || "Restore failed"); }
  };

  const [selected, setSelected] = useState(new Set());
  const [bulkOpen, setBulkOpen] = useState(false);
  const [bulkBusy, setBulkBusy] = useState(false);
  const allSelected = products.length > 0 && products.every((p) => selected.has(p.id));
  const toggleSelect = (id) => setSelected((s) => { const n = new Set(s); n.has(id) ? n.delete(id) : n.add(id); return n; });
  const toggleSelectAll = () => setSelected(allSelected ? new Set() : new Set(products.map((p) => p.id)));
  const doBulkArchive = async () => {
    setBulkBusy(true);
    try {
      const { data } = await api.post("/products/bulk-archive", { ids: [...selected] });
      toast.success(`Archived ${data.archived} product${data.archived === 1 ? "" : "s"}`);
      setBulkOpen(false); setSelected(new Set()); load();
    } catch (e) { toast.error(apiError(e.response?.data?.detail) || "Bulk archive failed"); }
    finally { setBulkBusy(false); }
  };
  const doBulkRestore = async () => {
    setBulkBusy(true);
    try {
      const { data } = await api.post("/products/bulk-restore", { ids: [...selected] });
      toast.success(`Restored ${data.restored} product${data.restored === 1 ? "" : "s"}`);
      setBulkOpen(false); setSelected(new Set()); load();
    } catch (e) { toast.error(apiError(e.response?.data?.detail) || "Bulk restore failed"); }
    finally { setBulkBusy(false); }
  };

  const loadProducts = () => {
    if (tab === "departments" || tab === "holds") return;
    const extra = fashion.isFashion && showArchivedDepts ? { include_archived_departments: "true" } : {};
    const stock = tab === "low" ? "low" : tab === "out" ? "out" : "";
    const ptype = tab === "non_stock" ? "non_stock" : "";
    const archived = tab === "archived" ? "true" : "";
    const cid = q ? beginInteraction("inventory", "inventory_search") : null;
    const seq = ++loadSeq.current;   // stale-response guard: only the latest page/search request may update the table
    // Phase A-0: one page at a time + exact server counts; the browser never downloads the whole catalogue.
    api.get("/products/page", { params: { search: q, stock, ptype, archived, page, page_size: PAGE_SIZE, ...extra } })
      .then((r) => { if (seq !== loadSeq.current) return; setProducts(r.data.items); setMeta({ total: r.data.total, pages: r.data.pages, counts: r.data.counts }); if (cid) requestAnimationFrame(() => endInteraction(cid, true)); })
      .catch(() => { if (cid) endInteraction(cid, false); });
  };
  // Reorder suggestions depend on stock levels, not on the search box: fetched when the Reorder tab is open and after stock mutations
  // on that tab (load() is called by save/adjust/delete/archive/restore/bulk/holds) — never per keystroke, never for other tabs.
  const loadReorder = () => { if (canManage && tab === "reorder") api.get("/products/reorder-list").then((r) => setReorder(r.data)).catch(() => {}); };
  const load = () => { loadProducts(); loadReorder(); };
  useEffect(() => { setPage(1); }, [q, tab, showArchivedDepts]);
  useEffect(() => { loadProducts(); setSelected(new Set()); }, [q, tab, showArchivedDepts, page]); // eslint-disable-line react-hooks/exhaustive-deps
  useEffect(() => { loadReorder(); }, [canManage, tab]); // eslint-disable-line react-hooks/exhaustive-deps

  const setCost = (v) => {
    const cost = parseFloat(v || 0);
    setForm((f) => {
      const next = { ...f, cost };
      if (f.pricing_method === "markup" && f.markup_pct !== "" && !isNaN(parseFloat(f.markup_pct))) {
        next.price = round2(cost * (1 + parseFloat(f.markup_pct) / 100));
      }
      return next;
    });
  };
  const setMarkup = (v) => {
    setForm((f) => {
      const next = { ...f, markup_pct: v };
      const m = parseFloat(v);
      if (v !== "" && !isNaN(m)) next.price = round2((parseFloat(f.cost) || 0) * (1 + m / 100));
      return next;
    });
  };
  const setPrice = (v) => {
    const price = parseFloat(v || 0);
    setForm((f) => {
      const next = { ...f, price };
      const cost = parseFloat(f.cost) || 0;
      if (f.pricing_method === "markup" && cost > 0) {
        next.markup_pct = String(round2(((price - cost) / cost) * 100));
      }
      return next;
    });
  };
  const setPricingMethod = (method) => {
    setForm((f) => {
      const next = { ...f, pricing_method: method };
      if (method === "markup") {
        const cost = parseFloat(f.cost) || 0;
        const price = parseFloat(f.price) || 0;
        if (f.markup_pct === "" && cost > 0 && price > 0) next.markup_pct = String(round2(((price - cost) / cost) * 100));
      }
      return next;
    });
  };

  const save = async () => {
    const cid = beginInteraction("inventory", "inventory_save");
    try {
      if (form.pricing_method === "markup" && form.markup_pct !== "" && parseFloat(form.markup_pct) < 0) {
        toast.error("Markup % cannot be negative"); return;
      }
      const payload = { ...form, markup_pct: form.markup_pct === "" || form.markup_pct === null ? null : parseFloat(form.markup_pct) };
      if (form.id) await api.put(`/products/${form.id}`, payload);
      else await api.post("/products", payload);
      toast.success("Product saved"); setEditOpen(false); endInteraction(cid, true); load();   // end before load(): load() may start an inventory_search interaction
    } catch (e) { endInteraction(cid, false); toast.error(apiError(e.response?.data?.detail)); }
  };

  const openAdjust = (p) => {
    setAdjTarget(p); setAdj({ dir: "add", amount: "", reason: "", note: "" }); setAdjHistory([]); setAdjustOpen(true);
    api.get(`/products/${p.id}/adjustments`).then((r) => setAdjHistory(r.data)).catch(() => setAdjHistory([]));
  };
  const saveAdjust = async () => {
    const amt = parseFloat(adj.amount || 0);
    if (!(amt > 0)) { toast.error("Enter an amount greater than zero"); return; }
    if (!adj.reason) { toast.error("Select an adjustment reason"); return; }
    if (adj.reason === "Other" && !adj.note.trim()) { toast.error("A note is required when the reason is 'Other'"); return; }
    const delta = adj.dir === "remove" ? -amt : amt;
    try {
      await api.post(`/products/${adjTarget.id}/adjust`, { delta, reason: adj.reason, note: adj.note });
      toast.success("Stock adjusted"); setAdjustOpen(false); load();
    } catch (e) { toast.error(apiError(e.response?.data?.detail)); }
  };

  const uploadImage = async (e) => {
    const f = e.target.files?.[0]; if (!f) return;
    const fd = new FormData(); fd.append("file", f);
    try {
      const { data } = await api.post("/products/image", fd, { headers: { "Content-Type": "multipart/form-data" } });
      setForm((prev) => ({ ...prev, image_path: data.image_path, image_url: data.image_url }));
      toast.success("Image uploaded");
    } catch (e2) { toast.error(apiError(e2.response?.data?.detail)); }
  };

  const exportReorder = () => {
    const rows = [["Product", "Supplier", "Available", "Reorder Level", "Suggested Qty", "Cost"]];
    reorder.forEach((p) => rows.push([p.name, p.supplier, p.quantity, p.reorder_level, p.suggested_qty, p.cost]));
    const csv = rows.map((r) => r.join(",")).join("\n");
    const url = URL.createObjectURL(new Blob([csv], { type: "text/csv" }));
    const a = document.createElement("a"); a.href = url; a.download = "reorder-list.csv"; a.click();
  };

  return (
    <div className="space-y-6">
      <div className="flex flex-wrap items-center justify-between gap-3">
        <div>
          <h1 className="font-head text-3xl font-extrabold">Inventory</h1>
          <p className="text-muted-foreground">Stock levels, low-stock alerts and reorder management.</p>
        </div>
        <div className="flex gap-2">
          {user?.role === "owner" && (
            <Button variant="outline" onClick={() => { setImpPrev(null); setImpFile(null); setImpOpen(true); }} data-testid="bulk-import-btn">
              Bulk Import
            </Button>
          )}
          <Button onClick={() => { setForm(blank); setEditOpen(true); }} className="bg-emerald-600 hover:bg-emerald-700 text-white" data-testid="add-product-btn">
            <Plus className="w-4 h-4 mr-1.5" /> Add Product
          </Button>
        </div>
      </div>

      <ReservedStockDialog product={reservedTarget} onClose={() => setReservedTarget(null)} />
      <Dialog open={impOpen} onOpenChange={setImpOpen}>
        <DialogContent className="max-w-lg max-h-[85vh] overflow-y-auto" data-testid="bulk-import-dialog">
          <DialogHeader><DialogTitle className="font-head">Bulk Import Inventory</DialogTitle></DialogHeader>
          <p className="text-xs text-muted-foreground">Upload .xlsx or .csv. Imports only into your business. Existing SKUs are never overwritten. They're skipped. Nothing is written until you press Confirm Import.</p>
          <input type="file" accept=".xlsx,.csv" onChange={(e) => e.target.files[0] && doPreview(e.target.files[0])} className="block text-sm mt-2" data-testid="bulk-import-file" />
          {impBusy && <p className="text-sm text-muted-foreground mt-2">Processing…</p>}
          {impPrev && (
            <div className="mt-3 space-y-1 text-sm" data-testid="bulk-import-preview">
              <div className="grid grid-cols-2 gap-1">
                <span>Total rows:</span><b>{impPrev.total_data_rows}</b>
                <span>Valid rows:</span><b>{impPrev.valid_rows}</b>
                <span>New SKUs:</span><b className="text-emerald-600">{impPrev.new_skus}</b>
                <span>Existing SKU collisions:</span><b className={impPrev.existing_sku_collisions ? "text-amber-600" : ""}>{impPrev.existing_sku_collisions}</b>
                <span>Categories:</span><b>{impPrev.category_count}</b>
                <span>General / Unassigned:</span><b>{impPrev.general_unassigned}</b>
                <span>All quantities = 0:</span><b>{impPrev.all_quantities_zero ? "Yes" : "No"}</b>
                <span>Stock products:</span><b data-testid="imp-stock-rows">{impPrev.stock_rows}</b>
                <span>Non-Stock / Special Order:</span><b className="text-sky-600" data-testid="imp-non-stock-rows">{impPrev.non_stock_rows}</b>
                <span>Validation errors:</span><b className={impPrev.validation_error_count ? "text-rose-600" : ""}>{impPrev.validation_error_count}</b>
              </div>
              {impPrev.type_conflict_rows > 0 && (
                <p className="text-xs text-amber-600 mt-1" data-testid="imp-type-conflict">{impPrev.type_conflict_rows} row(s) have a Product Type that conflicts with Track Inventory. Correct them in the file before importing.</p>
              )}
              {impPrev.rejected_rows?.length > 0 && (
                <div className="max-h-32 overflow-y-auto rounded border border-rose-500/30 text-xs mt-1" data-testid="imp-rejected-list">
                  {impPrev.rejected_rows.slice(0, 50).map((r) => <p key={r.row} className="px-2 py-1 border-b border-border last:border-0"><span className="text-muted-foreground">#{r.row}</span> {r.sku || "-"} · {r.name || "-"}: <span className="text-rose-600">{r.errors}</span></p>)}
                </div>
              )}
              {impPrev.sample_rows?.length > 0 && (
                <div className="max-h-40 overflow-y-auto rounded border border-border text-xs mt-1" data-testid="imp-sample-rows">
                  {impPrev.sample_rows.map((r) => (
                    <div key={r.row} className="px-2 py-1 border-b border-border last:border-0 flex justify-between gap-2">
                      <span className="truncate"><span className="text-muted-foreground">#{r.row}</span> {r.sku} · {r.name}</span>
                      <Badge className={r.product_type === "non_stock" ? "bg-sky-500/15 text-sky-500 border-sky-500/30" : "bg-emerald-500/15 text-emerald-600 border-emerald-500/30"}>{r.product_type === "non_stock" ? "Non-Stock / Special Order" : "Stock"}</Badge>
                    </div>
                  ))}
                </div>
              )}
              {impPrev.validation_error_count > 0 && (
                <p className="text-xs text-rose-600 mt-1">Import is all-or-nothing. Fix the {impPrev.validation_error_count} flagged row(s) before importing.</p>
              )}
              {impPrev.existing_sku_collisions > 0 && (
                <p className="text-xs text-amber-600 mt-1">{impPrev.existing_sku_collisions} SKU(s) already exist and will be skipped (not overwritten).</p>
              )}
              <Button className="w-full mt-3 bg-emerald-600 hover:bg-emerald-700 text-white"
                disabled={impBusy || impPrev.valid_rows === 0 || impPrev.validation_error_count > 0}
                onClick={doCommit} data-testid="bulk-import-confirm">
                Confirm Import ({impPrev.new_skus} new products)
              </Button>
            </div>
          )}
        </DialogContent>
      </Dialog>

      <div className="grid grid-cols-3 gap-4">
        <StatCard icon={Package} label="Total Products" value={meta.counts.all} color="text-sky-500" />
        <StatCard icon={AlertTriangle} label="Low Stock" value={meta.counts.low} color="text-amber-500" />
        <StatCard icon={PackageX} label="Out of Stock" value={meta.counts.out} color="text-rose-500" />
      </div>

      <div className="flex flex-wrap items-center gap-3 justify-between">
        <Tabs value={tab} onValueChange={setTab}>
          <TabsList>
            <TabsTrigger value="all" data-testid="tab-all">All</TabsTrigger>
            <TabsTrigger value="low" data-testid="tab-low">Low Stock</TabsTrigger>
            <TabsTrigger value="out" data-testid="tab-out">Out of Stock</TabsTrigger>
            <TabsTrigger value="reorder" data-testid="tab-reorder">Reorder List</TabsTrigger>
            <TabsTrigger value="non_stock" data-testid="tab-non-stock">Non-Stock / Special Order</TabsTrigger>
            {canManage && <TabsTrigger value="archived" data-testid="tab-archived">Archived</TabsTrigger>}
            {fashion.isFashion && canManage && <TabsTrigger value="departments" data-testid="tab-departments">Departments</TabsTrigger>}
            {fashion.isFashion && canManage && <TabsTrigger value="holds" data-testid="tab-holds">Return Holds</TabsTrigger>}
          </TabsList>
        </Tabs>
        {fashion.isFashion && canManage && fashion.departments.some((d) => d.archived) && !["departments", "holds", "reorder"].includes(tab) && (
          <label className="flex items-center gap-2 text-xs text-muted-foreground"><Checkbox checked={showArchivedDepts} onCheckedChange={(v) => setShowArchivedDepts(!!v)} data-testid="show-archived-depts" /> Include archived departments</label>
        )}
        <div className="relative flex-1 min-w-[200px] max-w-xs">
          <Search className="w-4 h-4 absolute left-3 top-1/2 -translate-y-1/2 text-muted-foreground" />
          <SearchBox onChange={setQ} placeholder="Search products…" className="pl-9" data-testid="inventory-search" />
        </div>
      </div>

      {canManage && tab !== "reorder" && selected.size > 0 && (
        <div className="flex items-center justify-between rounded-xl border border-border bg-secondary/40 px-4 py-2.5" data-testid="bulk-action-bar">
          <p className="text-sm font-semibold" data-testid="bulk-selected-count">{selected.size} product{selected.size === 1 ? "" : "s"} selected</p>
          <div className="flex gap-2">
            <Button size="sm" variant="ghost" onClick={() => setSelected(new Set())} data-testid="bulk-clear">Clear</Button>
            {tab === "archived" ? (
              <Button size="sm" className="bg-emerald-600 hover:bg-emerald-700 text-white" onClick={() => setBulkOpen(true)} data-testid="bulk-restore-btn"><ArchiveRestore className="w-4 h-4 mr-1.5" />Restore Selected</Button>
            ) : (
              <Button size="sm" className="bg-amber-600 hover:bg-amber-700 text-white" onClick={() => setBulkOpen(true)} data-testid="bulk-archive-btn"><Archive className="w-4 h-4 mr-1.5" />Archive Selected</Button>
            )}
          </div>
        </div>
      )}

      {tab === "departments" ? <DepartmentsPanel /> : tab === "holds" ? <ReturnHolds onChanged={load} /> : tab === "reorder" ? (
        <Card className="p-0 overflow-hidden">
          <div className="p-4 flex justify-between items-center border-b border-border">
            <p className="font-head font-bold">Reorder Suggestions ({reorder.length})</p>
            <Button variant="outline" size="sm" onClick={exportReorder} data-testid="export-reorder"><Download className="w-4 h-4 mr-1.5" /> Export CSV</Button>
          </div>
          <Table>
            <TableHeader><TableRow>
              <TableHead>Product</TableHead><TableHead>Supplier</TableHead><TableHead className="text-right">Available</TableHead>
              <TableHead className="text-right">Reorder At</TableHead><TableHead className="text-right">Suggested</TableHead><TableHead>Status</TableHead>
            </TableRow></TableHeader>
            <TableBody>
              {reorder.map((p) => (
                <TableRow key={p.id} data-testid={`reorder-row-${p.id}`}>
                  <TableCell className="font-medium">{p.name}</TableCell>
                  <TableCell className="text-muted-foreground">{p.supplier || "-"}</TableCell>
                  <TableCell className="text-right mono">{p.quantity}</TableCell>
                  <TableCell className="text-right mono">{p.reorder_level}</TableCell>
                  <TableCell className="text-right mono font-bold text-emerald-500">+{p.suggested_qty}</TableCell>
                  <TableCell><Badge className={stockBadge(p.stock_status)}>{p.stock_status}</Badge></TableCell>
                </TableRow>
              ))}
              {reorder.length === 0 && <TableRow><TableCell colSpan={6} className="text-center text-muted-foreground py-8">All stock levels healthy 🎉</TableCell></TableRow>}
            </TableBody>
          </Table>
        </Card>
      ) : (
        <Card className="p-0 overflow-hidden">
          <Table>
            <TableHeader><TableRow>
              {canManage && <TableHead className="w-10"><Checkbox checked={allSelected} onCheckedChange={toggleSelectAll} data-testid="select-all" /></TableHead>}
              <TableHead>Product</TableHead><TableHead>SKU</TableHead><TableHead className="text-right">Price</TableHead>
              <TableHead className="text-right">Available</TableHead><TableHead className="text-right">Low Level</TableHead><TableHead>Status</TableHead><TableHead></TableHead>
            </TableRow></TableHeader>
            <TableBody>
              {products.map((p) => (
                <TableRow key={p.id} data-testid={`product-row-${p.id}`}>
                  {canManage && <TableCell className="w-10"><Checkbox checked={selected.has(p.id)} onCheckedChange={() => toggleSelect(p.id)} data-testid={`select-${p.id}`} /></TableCell>}
                  <TableCell>
                    <div className="font-medium flex items-center gap-1.5">{p.name}{p.variant ? ` · ${p.variant}` : ""}
                      {p.is_parent && <Badge variant="outline" className="text-[10px] text-accent border-accent/40" data-testid={`parent-badge-${p.id}`}><Layers className="w-3 h-3 mr-1" />{p.variant_count || 0} variant{p.variant_count === 1 ? "" : "s"}</Badge>}
                      {p.department && deptOf(p)?.archived && <Badge variant="outline" className="text-[10px] text-amber-600 border-amber-500/40" data-testid={`dept-archived-badge-${p.id}`}>Dept. archived</Badge>}
                    </div>
                    <p className="text-xs text-slate-600 dark:text-slate-300">{p.department ? `${deptOf(p)?.name || p.department} · ` : ""}{p.category} · {p.brand || "No brand"}</p>
                  </TableCell>
                  <TableCell className="mono text-xs">{p.sku || "-"}</TableCell>
                  <TableCell className="text-right mono">{p.is_parent && p.price_min != null && p.price_min !== p.price_max ? `${money(p.price_min)} – ${money(p.price_max)}` : money(p.price)}</TableCell>
                  <TableCell className="text-right mono font-bold">{p.product_type === "non_stock" ? "-" : (<>
                    {p.quantity}
                    {p.reserved_qty > 0 && <button type="button" className="block ml-auto text-[10px] font-normal text-amber-600 dark:text-amber-400 whitespace-nowrap underline decoration-dotted underline-offset-2 hover:text-amber-700" title="See which orders hold this stock" onClick={(e) => { e.stopPropagation(); setReservedTarget(p); }} data-testid={`reserved-${p.id}`}>reserved {p.reserved_qty} · on hand {p.on_hand_qty}</button>}
                  </>)}</TableCell>
                  <TableCell className="text-right mono text-muted-foreground">{p.product_type === "non_stock" ? "-" : p.low_stock_level}</TableCell>
                  <TableCell>{p.product_type === "non_stock"
                    ? <Badge className="bg-sky-500/15 text-sky-500 border-sky-500/30" data-testid={`status-${p.id}`}>NON-STOCK</Badge>
                    : <Badge className={stockBadge(p.stock_status)} data-testid={`status-${p.id}`}>{p.stock_status}</Badge>}</TableCell>
                  <TableCell className="text-right whitespace-nowrap">
                    {tab === "archived" ? (
                      <Button size="sm" variant="ghost" className="text-emerald-600" onClick={() => doRestore(p)} data-testid={`restore-product-${p.id}`}><ArchiveRestore className="w-3.5 h-3.5 mr-1" />Restore</Button>
                    ) : (
                      <>
                        {p.is_parent
                          ? <Button size="sm" variant="ghost" className="text-accent" onClick={() => setVariantTarget(p)} data-testid={`variants-product-${p.id}`}><Layers className="w-3.5 h-3.5 mr-1" />Variants</Button>
                          : <Button size="sm" variant="ghost" onClick={() => openAdjust(p)} data-testid={`adjust-product-${p.id}`}><SlidersHorizontal className="w-3.5 h-3.5 mr-1" />Adjust</Button>}
                        <Button size="sm" variant="ghost" onClick={() => { setForm({ ...blank, ...p, pricing_method: p.pricing_method || "price", markup_pct: p.markup_pct ?? "", product_type: p.product_type || "stock" }); setEditOpen(true); }} data-testid={`edit-product-${p.id}`}>Edit</Button>
                        {canManage && <Button size="sm" variant="ghost" className="text-rose-500 hover:text-rose-600" onClick={() => openDelete(p)} data-testid={`delete-product-${p.id}`}><Trash2 className="w-3.5 h-3.5 mr-1" />Delete</Button>}
                      </>
                    )}
                  </TableCell>
                </TableRow>
              ))}
              {products.length === 0 && <TableRow><TableCell colSpan={canManage ? 8 : 7} className="text-center text-muted-foreground py-8">No products found.</TableCell></TableRow>}
            </TableBody>
          </Table>
          {meta.total > 0 && (
            <div className="flex items-center justify-between border-t border-border px-4 py-2.5 text-xs text-muted-foreground" data-testid="inventory-pagination">
              <span data-testid="inventory-page-summary">Showing {(page - 1) * PAGE_SIZE + 1}–{Math.min(page * PAGE_SIZE, meta.total)} of {meta.total.toLocaleString()} product{meta.total === 1 ? "" : "s"}</span>
              <div className="flex items-center gap-2">
                <Button size="sm" variant="outline" disabled={page <= 1} onClick={() => setPage((p) => Math.max(1, p - 1))} data-testid="inventory-prev-page">Previous</Button>
                <span data-testid="inventory-page-indicator">Page {page} of {meta.pages}</span>
                <Button size="sm" variant="outline" disabled={page >= meta.pages} onClick={() => setPage((p) => Math.min(meta.pages, p + 1))} data-testid="inventory-next-page">Next</Button>
              </div>
            </div>
          )}
        </Card>
      )}

      <Dialog open={editOpen} onOpenChange={setEditOpen}>
        <DialogContent className="max-w-lg max-h-[90vh] overflow-y-auto" data-testid="product-form">
          <DialogHeader><DialogTitle className="font-head">{form.id ? "Edit" : "Add"} Product</DialogTitle>
            <DialogDescription>{form.id ? "Update product details. Stock quantity is changed only through Adjust Stock." : "Add a new product to your inventory."}</DialogDescription>
          </DialogHeader>
          <div className="grid grid-cols-2 gap-3">
            <div className="col-span-2 flex items-center gap-3 rounded-xl border border-border p-3" data-testid="pf-image-block">
              <div className="w-16 h-16 rounded-lg overflow-hidden bg-secondary/60 flex items-center justify-center shrink-0">
                {imgSrc(form)
                  ? <img src={imgSrc(form)} alt="product" className="w-full h-full object-cover" />
                  : <Package className="w-6 h-6 text-muted-foreground/40" />}
              </div>
              <div className="flex-1">
                <Label className="text-xs">Product Image</Label>
                <input type="file" accept="image/jpeg,image/png,image/webp" onChange={uploadImage} className="block text-xs mt-1" data-testid="pf-image-upload" />
                <p className="text-[11px] text-muted-foreground mt-1">JPG, PNG or WebP up to 5MB. Auto-compressed. Shows on POS cards.</p>
              </div>
              {form.image_path && <Button size="sm" variant="ghost" className="text-rose-500" onClick={() => setForm({ ...form, image_path: null, image_url: null })} data-testid="pf-image-remove">Remove</Button>}
            </div>

            <div className="col-span-2 rounded-xl border border-border p-3" data-testid="pf-type-block">
              {fashion.isFashion && (
                <div className="mb-3 space-y-2" data-testid="pf-department-block">
                  <Label className="text-xs">Department</Label>
                  <select className="w-full h-9 rounded-md border border-border bg-background px-2 text-sm" value={form.department || ""} disabled={!!form.id}
                    onChange={(e) => setForm({ ...form, department: e.target.value || null, category: "General", details: {}, is_parent: e.target.value ? form.is_parent : false })} data-testid="pf-department">
                    <option value="">No department</option>
                    {fashion.active.map((d) => <option key={d.key} value={d.key}>{d.name}</option>)}
                  </select>
                  {form.id && form.department && <p className="text-[11px] text-muted-foreground">Department changes are an authorized correction — use "Correct department" below.</p>}
                  {form.department && !form.parent_id && (
                    <>
                      <div className="flex gap-2">
                        {[[false, "Simple product"], [true, "Has variants"]].map(([v, l]) => (
                          <Button key={String(v)} type="button" size="sm" variant={!!form.is_parent === v ? "default" : "outline"} disabled={!!form.id}
                            className={`flex-1 ${!!form.is_parent === v ? "bg-accent hover:bg-accent/90 text-accent-foreground" : ""}`}
                            onClick={() => setForm({ ...form, is_parent: v, product_type: "stock" })} data-testid={`pf-variant-mode-${v ? "parent" : "simple"}`}>{l}</Button>
                        ))}
                      </div>
                      {form.is_parent && <p className="text-[11px] text-muted-foreground" data-testid="pf-parent-hint">Each {deptOf(form)?.attributes.map((a) => a.label.toLowerCase()).join(" / ") || "combination"} becomes its own SKU with price and stock (added after saving via "Variants"). The parent itself holds no stock.</p>}
                      {(deptOf(form)?.parent_fields || []).map((pf) => (
                        <div key={pf.key}>
                          <Label className="text-xs">{pf.label}</Label>
                          {pf.options
                            ? <select className="w-full h-9 rounded-md border border-border bg-background px-2 text-sm" value={form.details?.[pf.key] || ""} onChange={(e) => setForm({ ...form, details: { ...(form.details || {}), [pf.key]: e.target.value } })} data-testid={`pf-detail-${pf.key}`}>
                                <option value="">—</option>{pf.options.map((o) => <option key={o} value={o}>{o}</option>)}</select>
                            : <Input value={form.details?.[pf.key] || ""} onChange={(e) => setForm({ ...form, details: { ...(form.details || {}), [pf.key]: e.target.value } })} data-testid={`pf-detail-${pf.key}`} />}
                        </div>
                      ))}
                    </>
                  )}
                </div>
              )}
              {!form.is_parent && <><Label className="text-xs">Product Type</Label></>}
              {!form.is_parent && <div className="flex gap-2 mt-1.5">
                {[["stock", "Stocked"], ["non_stock", "Non-Stock / Special Order"]].map(([v, l]) => (
                  <Button key={v} type="button" size="sm" variant={(form.product_type || "stock") === v ? "default" : "outline"}
                    className={`flex-1 ${(form.product_type || "stock") === v ? "bg-emerald-600 hover:bg-emerald-700 text-white" : ""}`}
                    onClick={() => setForm({ ...form, product_type: v })} data-testid={`pf-type-${v}`}>{l}</Button>
                ))}
              </div>}
              {(form.product_type === "non_stock") && (
                <p className="text-[11px] text-muted-foreground mt-2" data-testid="pf-type-hint">
                  Sold but not kept in stock (e.g. sand, sourced materials). Stock is never tracked or deducted, and zero on-hand never blocks a sale. Customers see it as a normal product.
                </p>
              )}
            </div>

            <div className="col-span-2 rounded-xl border border-border p-3 space-y-3" data-testid="pf-pricing-block">
              <div>
                <Label className="text-xs">Pricing Method</Label>
                <div className="flex gap-2 mt-1">
                  <Button type="button" size="sm" variant={form.pricing_method !== "markup" ? "default" : "outline"}
                    className={`flex-1 ${form.pricing_method !== "markup" ? "bg-emerald-600 hover:bg-emerald-700 text-white" : ""}`}
                    onClick={() => setPricingMethod("price")} data-testid="pf-method-price">Enter Selling Price</Button>
                  <Button type="button" size="sm" variant={form.pricing_method === "markup" ? "default" : "outline"}
                    className={`flex-1 ${form.pricing_method === "markup" ? "bg-emerald-600 hover:bg-emerald-700 text-white" : ""}`}
                    onClick={() => setPricingMethod("markup")} data-testid="pf-method-markup">Use Markup %</Button>
                </div>
              </div>
              <div className="grid grid-cols-2 gap-3">
                <div>
                  <Label className="text-xs">Cost Price</Label>
                  <Input type="number" step="0.01" value={form.cost} data-testid="pf-cost"
                    onChange={(e) => setCost(e.target.value)} />
                </div>
                {form.pricing_method === "markup" && (
                  <div>
                    <Label className="text-xs">Markup %</Label>
                    <Input type="number" step="0.01" min="0" value={form.markup_pct} data-testid="pf-markup"
                      placeholder="e.g. 12.25" onChange={(e) => setMarkup(e.target.value)} />
                  </div>
                )}
                <div className={form.pricing_method === "markup" ? "col-span-2" : ""}>
                  <Label className="text-xs">Selling Price{form.pricing_method === "markup" ? " (editable)" : ""}</Label>
                  <Input type="number" step="0.01" value={form.price} data-testid="pf-price"
                    onChange={(e) => setPrice(e.target.value)} />
                </div>
              </div>
              <p className="text-xs text-muted-foreground" data-testid="pf-margin-indicator">
                Gross Margin: <span className="mono font-semibold text-foreground">{grossMargin(parseFloat(form.cost) || 0, parseFloat(form.price) || 0).toFixed(2)}%</span>
                {form.pricing_method === "markup" && form.markup_pct !== "" && !isNaN(parseFloat(form.markup_pct)) &&
                  <> · Effective Markup: <span className="mono font-semibold text-foreground">{parseFloat(form.markup_pct).toFixed(2)}%</span></>}
              </p>
            </div>
            <BulkTiersEditor form={form} setForm={setForm} showCost={user?.role === "owner" || user?.role === "manager"} />
            {[
              ["name", "Name", "text", 2], ["category", "Category", "text"], ["variant", "Variant/Size", "text"], ["model", "Model", "text"],
              ["brand", "Brand", "text"], ["supplier", "Supplier", "text"],
              ["barcode", "Barcode", "text"], ["sku", "SKU", "text"],
              ["quantity", "Quantity", "number"], ["reorder_level", "Reorder Level", "number"],
              ["low_stock_level", "Low Stock Level", "number"], ["location", "Location", "text"], ["unit", "Unit", "text"],
            ].filter(([k]) => form.product_type === "non_stock" || form.is_parent ? !["quantity", "reorder_level", "low_stock_level"].includes(k) : true)
              .filter(([k]) => form.is_parent ? !["variant", "barcode", "sku"].includes(k) : true)
              .filter(([k]) => form.parent_id ? !["name", "category", "brand", "supplier", "unit"].includes(k) : true).map(([k, lbl, type, span]) => (
              <div key={k} className={span === 2 ? "col-span-2" : ""}>
                <Label className="text-xs">{lbl}{k === "quantity" && form.id ? " (use Adjust)" : ""}</Label>
                {k === "category" && form.department && deptOf(form) ? (
                  <select className="w-full h-9 rounded-md border border-border bg-background px-2 text-sm" value={form.category || ""} onChange={(e) => setForm({ ...form, category: e.target.value })} data-testid="pf-category">
                    {!deptOf(form).categories.some((c) => c.name === form.category) && <option value={form.category || "General"}>{form.category || "General"}</option>}
                    {deptOf(form).categories.map((c) => <option key={c.id} value={c.name}>{c.name}</option>)}
                  </select>
                ) : (
                <Input type={type} value={form[k]} data-testid={`pf-${k}`}
                  disabled={k === "quantity" && !!form.id}
                  title={k === "quantity" && form.id ? "Stock changes must go through Adjust Stock with a reason" : undefined}
                  onChange={(e) => setForm({ ...form, [k]: type === "number" ? parseFloat(e.target.value || 0) : e.target.value })} />
                )}
              </div>
            ))}
          </div>
          {fashion.isFashion && form.id && form.department && !form.parent_id && <DepartmentCorrection form={form} fashion={fashion} onDone={() => { setEditOpen(false); load(); }} />}
          <Button onClick={save} className="bg-emerald-600 hover:bg-emerald-700 text-white" data-testid="save-product">Save Product</Button>
        </DialogContent>
      </Dialog>
      <VariantManager parent={variantTarget} dept={deptOf(variantTarget)} open={!!variantTarget} onOpenChange={(o) => !o && setVariantTarget(null)} onChanged={load} />

      <Dialog open={adjustOpen} onOpenChange={setAdjustOpen}>
        <DialogContent className="max-w-sm" data-testid="adjust-stock-modal">
          <DialogHeader>
            <DialogTitle className="font-head">Adjust Stock</DialogTitle>
            <DialogDescription>{adjTarget ? `${adjTarget.name}: available ${adjTarget.quantity}${adjTarget.reserved_qty > 0 ? ` (+${adjTarget.reserved_qty} reserved for pickup)` : ""}` : ""} · a reason is required and logged in the Audit Log.</DialogDescription>
          </DialogHeader>
          <div className="flex gap-2">
            <Button variant={adj.dir === "add" ? "default" : "outline"} className={`flex-1 ${adj.dir === "add" ? "bg-emerald-600 hover:bg-emerald-700 text-white" : ""}`} onClick={() => setAdj({ ...adj, dir: "add" })} data-testid="adj-add">Add (+)</Button>
            <Button variant={adj.dir === "remove" ? "default" : "outline"} className={`flex-1 ${adj.dir === "remove" ? "bg-rose-600 hover:bg-rose-700 text-white" : ""}`} onClick={() => setAdj({ ...adj, dir: "remove" })} data-testid="adj-remove">Remove (−)</Button>
          </div>
          <Label className="text-xs">Quantity</Label>
          <Input type="number" value={adj.amount} onChange={(e) => setAdj({ ...adj, amount: e.target.value })} className="mono" data-testid="adj-amount" />
          <Label className="text-xs">Reason</Label>
          <select value={adj.reason} onChange={(e) => setAdj({ ...adj, reason: e.target.value })} className="w-full h-10 rounded-md border border-input bg-background px-3 text-sm" data-testid="adj-reason">
            <option value="">Select a reason…</option>
            {ADJUST_REASONS.map((r) => <option key={r} value={r}>{r}</option>)}
          </select>
          {adj.reason === "Other" && (
            <Input value={adj.note} onChange={(e) => setAdj({ ...adj, note: e.target.value })} placeholder="Describe the reason" data-testid="adj-note" />
          )}
          {adj.amount && adjTarget && (
            <p className="text-sm text-muted-foreground">New available: <span className="mono font-bold text-foreground">{(adjTarget.quantity || 0) + (adj.dir === "remove" ? -1 : 1) * parseFloat(adj.amount || 0)}</span></p>
          )}
          <Button onClick={saveAdjust} className="bg-emerald-600 hover:bg-emerald-700 text-white" data-testid="save-adjust">Apply Adjustment</Button>
          {adjHistory.length > 0 && (
            <div className="mt-2 border-t border-border pt-2" data-testid="adjust-history">
              <p className="text-xs font-semibold text-muted-foreground mb-1">Recent adjustments</p>
              <div className="max-h-40 overflow-auto space-y-1">
                {adjHistory.map((h) => (
                  <div key={h.id} className="flex items-center justify-between text-xs">
                    <span className="text-muted-foreground">{(h.at || "").slice(0, 10)}</span>
                    <span className={`mono font-semibold ${h.delta < 0 ? "text-rose-500" : "text-emerald-500"}`}>{h.delta > 0 ? "+" : ""}{h.delta}</span>
                    <span className="flex-1 mx-2 truncate">{h.reason}{h.note ? `: ${h.note}` : ""}</span>
                    <span className="text-muted-foreground">{h.by}</span>
                  </div>
                ))}
              </div>
            </div>
          )}
        </DialogContent>
      </Dialog>
      <Dialog open={delOpen} onOpenChange={setDelOpen}>
        <DialogContent className="max-w-sm" data-testid="delete-product-modal">
          <DialogHeader>
            <DialogTitle className="font-head">{delHasHistory ? "Archive Product" : "Delete Product"}</DialogTitle>
            <DialogDescription>
              {delHasHistory
                ? "This product has transaction history and cannot be permanently deleted. Archive it instead?"
                : delTarget ? `Delete ${delTarget.name}? This action cannot be undone.` : ""}
            </DialogDescription>
          </DialogHeader>
          {delHasHistory ? (
            <div className="space-y-3">
              <p className="text-xs text-muted-foreground">Archiving hides it from POS sales and active inventory, but keeps all invoices, refunds, reports and audit records intact. You can restore it later from the Archived tab.</p>
              <Label className="text-xs">Reason (optional)</Label>
              <Input value={delReason} onChange={(e) => setDelReason(e.target.value)} placeholder="e.g. Discontinued" data-testid="archive-reason" />
              <div className="flex gap-2">
                <Button variant="outline" className="flex-1" onClick={() => setDelOpen(false)} data-testid="archive-cancel">Cancel</Button>
                <Button className="flex-1 bg-amber-600 hover:bg-amber-700 text-white" disabled={delBusy} onClick={doArchive} data-testid="archive-confirm"><Archive className="w-4 h-4 mr-1.5" />Archive Product</Button>
              </div>
            </div>
          ) : (
            <div className="space-y-3">
              <Label className="text-xs">Deletion reason (required)</Label>
              <Input value={delReason} onChange={(e) => setDelReason(e.target.value)} placeholder="Why are you deleting this product?" data-testid="delete-reason" />
              <div className="flex gap-2">
                <Button variant="outline" className="flex-1" onClick={() => setDelOpen(false)} data-testid="delete-cancel">Cancel</Button>
                <Button className="flex-1 bg-rose-600 hover:bg-rose-700 text-white" disabled={delBusy} onClick={doDelete} data-testid="delete-confirm"><Trash2 className="w-4 h-4 mr-1.5" />Delete Permanently</Button>
              </div>
            </div>
          )}
        </DialogContent>
      </Dialog>
      <Dialog open={bulkOpen} onOpenChange={setBulkOpen}>
        <DialogContent className="max-w-sm" data-testid="bulk-confirm-modal">
          <DialogHeader>
            <DialogTitle className="font-head">{tab === "archived" ? "Restore Products" : "Archive Products"}</DialogTitle>
            <DialogDescription>
              {tab === "archived"
                ? `Restore ${selected.size} product${selected.size === 1 ? "" : "s"} back into active inventory?`
                : `Archive ${selected.size} product${selected.size === 1 ? "" : "s"}? They'll be hidden from POS sales and active inventory. All invoices, refunds, reports and audit records stay intact, and you can restore them anytime.`}
            </DialogDescription>
          </DialogHeader>
          <div className="flex gap-2">
            <Button variant="outline" className="flex-1" onClick={() => setBulkOpen(false)} data-testid="bulk-cancel">Cancel</Button>
            {tab === "archived" ? (
              <Button className="flex-1 bg-emerald-600 hover:bg-emerald-700 text-white" disabled={bulkBusy} onClick={doBulkRestore} data-testid="bulk-restore-confirm"><ArchiveRestore className="w-4 h-4 mr-1.5" />Restore {selected.size}</Button>
            ) : (
              <Button className="flex-1 bg-amber-600 hover:bg-amber-700 text-white" disabled={bulkBusy} onClick={doBulkArchive} data-testid="bulk-archive-confirm"><Archive className="w-4 h-4 mr-1.5" />Archive {selected.size}</Button>
            )}
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
}

function StatCard({ icon: Icon, label, value, color }) {
  return (
    <Card className="p-4 flex items-center gap-3">
      <Icon className={`w-8 h-8 ${color}`} />
      <div><p className="text-xs uppercase tracking-wider text-muted-foreground">{label}</p><p className="mono text-2xl font-bold">{value}</p></div>
    </Card>
  );
}
